import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { typedFetch } from '../../typedFetch';
import { PrettyDate } from '../PrettyDate';
import './Schedules.scss';
export const Schedules = ({ appendToErrorLog, loggedInUser, }) => {
    const [loading, setLoading] = useState(true);
    const [schedules, setSchedules] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [showDepartments, setShowDepartments] = useState([]);
    useEffect(() => {
        if (!loggedInUser) {
            appendToErrorLog('Only logged-in users may see the list of schedules');
            setLoading(false);
            return;
        }
        typedFetch('schedule', {
            body: {
                action: 'get',
            },
        })
            .then((result) => {
            if (result.success) {
                setSchedules(result.schedules);
                setDepartments(result.departments);
                setShowDepartments(result.departments);
            }
            else {
                appendToErrorLog(`Failed to retrieve list of schedules: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while retrieving list of schedules: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    }, [loggedInUser, appendToErrorLog]);
    const sortingFunctions = useMemo(() => ({
        linkAZ: (schedule) => schedule.ticketName,
        timestampCreated: (schedule) => String(schedule.timestampCreated),
        timestampUpdated: (schedule) => String(schedule.timestampUpdated),
        frequencyInDays: (schedule) => String(schedule.frequencyInDays),
    }), []);
    const [sortType, setSortType] = useState(() => {
        const sortingStore = localStorage.getItem('schedulesSorting');
        if (sortingStore && Object.hasOwn(sortingFunctions, sortingStore)) {
            return sortingStore;
        }
        return 'timestampCreated';
    });
    useEffect(() => {
        localStorage.setItem('schedulesSorting', sortType);
    }, [sortType]);
    const [reverseSorting, setReverseSorting] = useState(localStorage.getItem('schedulesReverseSorting') === 'yes');
    useEffect(() => {
        localStorage.setItem('schedulesReverseSorting', reverseSorting ? 'yes' : '');
    }, [reverseSorting]);
    useEffect(() => {
        const newSchedules = schedules
            .toSorted((a, b) => sortingFunctions[sortType](reverseSorting ? b : a).localeCompare(sortingFunctions[sortType](reverseSorting ? a : b), 'en', { numeric: true }));
        setSchedules(newSchedules);
    }, [
        sortType,
        reverseSorting,
        schedules,
        sortingFunctions,
    ]);
    const schedulesToDisplay = useMemo(() => schedules.filter(schedule => showDepartments.includes(schedule.department)), [
        schedules,
        showDepartments,
    ]);
    if (loading) {
        return 'Processing...';
    }
    if (!loggedInUser) {
        return 'No schedules to display';
    }
    return (_jsxs("main", { className: "schedules", children: [_jsx("div", { children: _jsx("a", { href: "/schedules/new", children: _jsx("button", { children: "Create schedule" }) }) }), _jsxs("div", { className: "table", children: [_jsx("div", { className: "header", children: _jsxs("div", { className: "columns", children: [_jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                    if (sortType === 'linkAZ') {
                                                        setReverseSorting(!reverseSorting);
                                                    }
                                                    else {
                                                        setSortType('linkAZ');
                                                        setReverseSorting(false);
                                                    }
                                                }, children: sortType === 'linkAZ' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Link" })] }), _jsxs("div", { children: [_jsx("div", { className: "option checkboxes", children: departments.map(department => (_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: showDepartments.includes(department), onChange: (e) => {
                                                            setShowDepartments(e.currentTarget.checked
                                                                ? [...showDepartments, department]
                                                                : showDepartments.filter(dept => dept !== department));
                                                        } }), _jsx("div", { children: department })] }, department))) }), _jsx("div", { children: "Department" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                    if (sortType === 'timestampCreated') {
                                                        setReverseSorting(!reverseSorting);
                                                    }
                                                    else {
                                                        setSortType('timestampCreated');
                                                        setReverseSorting(false);
                                                    }
                                                }, children: sortType === 'timestampCreated' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Date created" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                    if (sortType === 'timestampUpdated') {
                                                        setReverseSorting(!reverseSorting);
                                                    }
                                                    else {
                                                        setSortType('timestampUpdated');
                                                        setReverseSorting(false);
                                                    }
                                                }, children: sortType === 'timestampUpdated' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Date updated" })] }), _jsxs("div", { children: [_jsx("div", { className: "option", children: _jsx("button", { onClick: () => {
                                                    if (sortType === 'frequencyInDays') {
                                                        setReverseSorting(!reverseSorting);
                                                    }
                                                    else {
                                                        setSortType('frequencyInDays');
                                                        setReverseSorting(false);
                                                    }
                                                }, children: sortType === 'frequencyInDays' ? (reverseSorting ? '↑' : '↓') : '○' }) }), _jsx("div", { children: "Frequency" })] })] }) }), schedulesToDisplay.map(schedule => (_jsx("div", { className: "schedule", children: _jsxs("div", { className: "columns", children: [_jsx("div", { children: _jsx("a", { href: `/schedules/${schedule.id}`, children: schedule.ticketName }) }), _jsx("div", { children: schedule.department }), _jsx("div", { children: _jsx(PrettyDate, { timestamp: schedule.timestampCreated, showRelativeDates: false }) }), _jsx("div", { children: _jsx(PrettyDate, { timestamp: schedule.timestampUpdated, showRelativeDates: false }) }), _jsx("div", { children: schedule.frequencyInDays })] }) }, schedule.id)))] })] }));
};
